import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle } from "lucide-react";
import { business } from "../content/site";
import { trackEvent, analyticsConfigured } from "../lib/analytics";
import { useLp } from "../i18n/lang";
import { useCopy } from "../i18n/copy";
import { useServices, useProblems } from "../i18n/content";
// Problemas destacados en el footer (los más relevantes para el usuario y la captación).
const FEATURED_PROBLEMS = [
  "catastro-escritura-no-coinciden",
  "no-se-donde-termina-mi-finca",
  "mi-finca-tiene-mas-o-menos-metros",
  "comprar-finca-comprobar-superficie",
  "construccion-no-documentada",
  "necesito-gml",
  "dividir-finca-en-parcelas",
];

export const Footer = () => {
  const lp = useLp();
  const f = useCopy("footer");
  const n = useCopy("nav");
  const services = useServices();
  const problems = useProblems();

  const companyLinks = [
    { label: n.professionals, href: "/profesionales/" },
    { label: n.about, href: "/sobre-midefinca/" },
    { label: n.contact, href: "/contacto/" },
    { label: f.prices, href: "/precios/" },
    { label: f.legal, href: "/aviso-legal/" },
    { label: f.privacy, href: "/politica-de-privacidad/" },
    { label: f.cookies, href: "/politica-de-cookies/" },
  ];

  return (
    <footer className="bg-obsidian text-slate-300" data-testid="site-footer">
      <div className="container-site py-14 lg:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <Link to={lp("/")} data-testid="footer-brand-logo" aria-label="MideFinca - inicio" className="inline-block">
              <img src="/brand/midefinca-logo-nav-claro.png" alt="MideFinca" width="1077" height="224" className="w-auto h-14" />
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-[#C2CFC5]" data-testid="footer-tagline">{f.tagline}</p>
            <div className="mt-5 flex flex-col gap-2.5 text-sm">
              <a href={business.phoneHref} onClick={() => trackEvent("phone_click", { location: "footer" })}
                 className="flex items-center gap-2 hover:text-terracotta transition-colors" data-testid="footer-phone">
                <Phone size={14} aria-hidden="true" /> {business.phoneDisplay}
              </a>
              <a href={`mailto:${business.email}`} onClick={() => trackEvent("email_click", { location: "footer" })}
                 className="flex items-center gap-2 hover:text-terracotta transition-colors" data-testid="footer-email">
                <Mail size={14} aria-hidden="true" /> {business.email}
              </a>
              <a href={business.whatsappUrl} target="_blank" rel="noopener noreferrer"
                 onClick={() => trackEvent("whatsapp_click", { location: "footer" })}
                 className="flex items-center gap-2 hover:text-terracotta transition-colors" data-testid="footer-whatsapp">
                <MessageCircle size={14} aria-hidden="true" /> WhatsApp
              </a>
            </div>
          </div>
          <nav aria-label={f.servicesAria}>
            <h2 className="tech-label !text-[#A9BCAE] mb-4">{f.servicesH}</h2>
            <ul className="space-y-2 text-sm">
              {services.slice(0, 8).map((s) => (
                <li key={s.slug}>
                  <Link to={lp(`/servicios/${s.slug}/`)} className="hover:text-paper transition-colors">{s.shortName}</Link>
                </li>
              ))}
            </ul>
          </nav>
          <nav aria-label={f.problemsAria}>
            <h2 className="tech-label !text-[#A9BCAE] mb-4">{f.problemsH}</h2>
            <ul className="space-y-2 text-sm">
              {FEATURED_PROBLEMS.map((slug) => problems.find((p) => p.slug === slug)).filter(Boolean).map((p) => (
                <li key={p.slug}>
                  <Link to={lp(`/problemas/${p.slug}/`)} className="hover:text-paper transition-colors">{p.shortName}</Link>
                </li>
              ))}
              <li>
                <Link to={lp("/problemas/")} data-testid="footer-all-problems"
                      className="inline-block mt-1 text-paper/90 font-medium hover:text-paper transition-colors">
                  {f.seeAllProblems} →
                </Link>
              </li>
            </ul>
          </nav>
          <nav aria-label={f.companyAria}>
            <h2 className="tech-label !text-[#A9BCAE] mb-4">{f.companyH}</h2>
            <ul className="space-y-2 text-sm">
              {companyLinks.map((item) => (
                <li key={item.href}>
                  <Link to={lp(item.href)} className="hover:text-paper transition-colors">{item.label}</Link>
                </li>
              ))}
              {analyticsConfigured && (
                <li>
                  <button data-testid="footer-cookie-settings"
                          onClick={() => window.dispatchEvent(new Event("mf:open-cookie-settings"))}
                          className="hover:text-paper transition-colors text-left">
                    {f.cookieSettings}
                  </button>
                </li>
              )}
            </ul>
          </nav>
        </div>
        <div className="mt-12 pt-6 border-t border-[#41705F] flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#A9BCAE]">
          <p>© {new Date().getFullYear()} MideFinca · Canarias</p>
          <p className="font-mono tracking-wider">CANARIAS · UTM 28R</p>
        </div>
      </div>
    </footer>
  );
};
